import Cookies from 'js-cookie';

type PrivacyGroup = {
  id: string;
  name: string;
  isAllowed: boolean;
};

type PrivacyGroups = {
  C0001?: PrivacyGroup;
  C0002?: PrivacyGroup;
  C0003?: PrivacyGroup;
  C0004?: PrivacyGroup;
};

class PrivacyService {
  #poolInterval = 250;
  #timeout;
  #startTime;
  #maxTimeout = 5000;
  #callbacks: Array<(privacy?: PrivacyService) => void> = [];

  privacyGroups: PrivacyGroups = {
    C0001: {
      id: 'C0001',
      name: 'Strictly Necessary Cookies',
      isAllowed: false
    },
    C0002: { id: 'C0002', name: 'Performance Cookies', isAllowed: false },
    C0003: { id: 'C0003', name: 'Functional Cookies', isAllowed: false },
    C0004: { id: 'C0004', name: 'Targeting Cookies', isAllowed: false }
  };

  #getOnetrustActiveGroups = async () => {
    const hasTimedOut = Date.now() - this.#startTime > this.#maxTimeout;

    if (window.OnetrustActiveGroups) {
      return window.OnetrustActiveGroups.split(',').filter((g) => !!g);
    }

    if (!hasTimedOut) {
      await new Promise((resolve) => {
        this.#timeout = setTimeout(resolve, this.#poolInterval);
      });
      return this.#getOnetrustActiveGroups();
    }

    clearTimeout(this.#timeout);
    return null;
  };

  #getCookieConsentActiveGroups = () => {
    const cookieConsent = Cookies.get('OptanonConsent');

    const parsedCookieConsent = new URLSearchParams(
      decodeURIComponent(cookieConsent)
    )
      .get('groups')
      ?.split(',')
      .reduce((acc: string[], g) => {
        const [group, value] = g.split(':');
        const isAllowed = value === '1';
        return [...acc, ...(isAllowed ? [group] : [])];
      }, []);

    return parsedCookieConsent?.length ? parsedCookieConsent : null;
  };

  #getGroups = async () => {
    return (
      this.#getCookieConsentActiveGroups() ||
      (await this.#getOnetrustActiveGroups()) ||
      []
    );
  };

  #updateGroups = async () => {
    const onetrustActiveGroups = await this.#getGroups();

    this.privacyGroups = Object.entries(this.privacyGroups).reduce(
      (acc, [key, value]) => {
        return {
          ...acc,
          [key]: {
            ...value,
            isAllowed: !!onetrustActiveGroups?.includes(key)
          }
        };
      },
      {}
    );
  };

  init = async (
    callback: (privacy: PrivacyService) => void | Promise<void> = () => {}
  ) => {
    this.#startTime = Date.now();
    await this.#updateGroups();
    await callback(this);

    const privacyChangeHandler = async () => {
      await this.#updateGroups();
      await callback(this);
      this.#callbacks.forEach((c) => c(this));
    };

    document.removeEventListener('OneTrustGroupsUpdated', privacyChangeHandler);
    document.addEventListener('OneTrustGroupsUpdated', privacyChangeHandler);

    return this;
  };

  registerCallback = (callback: (privacy?: PrivacyService) => void) => {
    const shouldRegister = !this.#callbacks.includes(callback);
    shouldRegister && this.#callbacks.push(callback);
  };

  isGroupAllowed = (group) => {
    return !!this.privacyGroups[group]?.isAllowed;
  };

  isDataDogAllowed = () => {
    return this.isGroupAllowed(this.privacyGroups?.C0002?.id);
  };

  isAdobeTargetAllowed = () => {
    return this.isGroupAllowed(this.privacyGroups?.C0004?.id);
  };
}

const privacy = new PrivacyService();

export default privacy;
